import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Upload, Image, Code, DollarSign, Package, TrendingUp, Users, Eye, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, loading: authLoading, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'productivity',
    type: 'app',
    featured: false
  });

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (!user || !isAdmin) {
        navigate('/login');
        toast.error('Admin access required');
      } else {
        fetchProducts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, isAdmin, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUploadForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/products`,
        {
          name: uploadForm.name,
          description: uploadForm.description,
          price: parseFloat(uploadForm.price),
          category: uploadForm.category,
          type: uploadForm.type,
          featured: uploadForm.featured
        },
        { withCredentials: true }
      );
      
      toast.success('Product uploaded successfully!', {
        description: `${data.name} has been added to the marketplace`
      });
      
      setUploadForm({
        name: '',
        description: '',
        price: '',
        category: 'productivity',
        type: 'app',
        featured: false
      });
      
      fetchProducts();
    } catch (error) {
      toast.error('Failed to upload product', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Products', value: products.length, icon: Package, color: 'from-blue-500 to-cyan-500' },
    { label: 'Total Revenue', value: '$12,450', icon: DollarSign, color: 'from-green-500 to-emerald-500' },
    { label: 'Active Users', value: '1,234', icon: Users, color: 'from-purple-500 to-pink-500' },
    { label: 'Page Views', value: '45.2K', icon: Eye, color: 'from-orange-500 to-red-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 flex justify-between items-center">
          <div>
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Admin Dashboard
              </span>
            </h1>
            <p className="text-xl text-gray-600">
              Welcome back, {user?.name || 'Admin'}!
            </p>
          </div>
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={stat.label} className="overflow-hidden border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="upload" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="upload" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Upload className="w-4 h-4 mr-2" />
              Upload Product
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              My Products
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Upload New Product</CardTitle>
                <CardDescription>Add a new app or content piece to your marketplace</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpload} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="type">Product Type</Label>
                      <select
                        id="type"
                        name="type"
                        value={uploadForm.type}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="app">App</option>
                        <option value="content">Content</option>
                        <option value="template">Template</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        name="category"
                        value={uploadForm.category}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="productivity">Productivity</option>
                        <option value="health">Health & Fitness</option>
                        <option value="finance">Finance</option>
                        <option value="design">Design</option>
                        <option value="education">Education</option>
                        <option value="marketing">Marketing</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Product Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={uploadForm.name}
                      onChange={handleInputChange}
                      placeholder="Enter product name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={uploadForm.description}
                      onChange={handleInputChange}
                      placeholder="Describe your product"
                      rows={4}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">Price ($)</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={uploadForm.price}
                      onChange={handleInputChange}
                      placeholder="29.99"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="featured">
                      <input
                        type="checkbox"
                        id="featured"
                        name="featured"
                        checked={uploadForm.featured}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Feature this product
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image">Product Image</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-500 transition-colors cursor-pointer">
                      <Image className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                      <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      <input type="file" className="hidden" id="image" accept="image/*" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="files">Product Files (ZIP, APK, etc.)</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-500 transition-colors cursor-pointer">
                      <Code className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Upload product files</p>
                      <p className="text-sm text-gray-500">Any file type up to 100MB</p>
                      <input type="file" className="hidden" id="files" multiple />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload Product
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">My Products</CardTitle>
                <CardDescription>Manage your listed apps and content</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {products.slice(0, 5).map((app) => (
                    <div key={app.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        {app.image && (
                          <img src={app.image} alt={app.name} className="w-16 h-16 rounded-lg object-cover" />
                        )}
                        <div>
                          <h3 className="font-semibold text-lg">{app.name}</h3>
                          <p className="text-sm text-gray-600">{app.category}</p>
                          <div className="flex items-center space-x-4 mt-1">
                            <Badge variant="outline" className="text-xs">{app.downloads}</Badge>
                            <span className="text-sm text-gray-500">Rating: {app.rating}</span>
                            {app.featured && (
                              <Badge className="bg-yellow-500 text-white text-xs">Featured</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-orange-600">${app.price}</div>
                          <div className="text-sm text-gray-500">per sale</div>
                        </div>
                        <Button variant="outline" size="sm" className="border-orange-500 text-orange-600 hover:bg-orange-50">
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No products yet. Upload your first product!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Sales Overview</CardTitle>
                  <CardDescription>Last 30 days performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Sales</span>
                      <span className="text-2xl font-bold text-green-600">$12,450</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Units Sold</span>
                      <span className="text-2xl font-bold">342</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Avg. Order Value</span>
                      <span className="text-2xl font-bold">$36.40</span>
                    </div>
                    <div className="h-48 bg-gradient-to-t from-orange-100 to-transparent rounded-lg flex items-end justify-center">
                      <div className="text-center pb-8">
                        <TrendingUp className="w-16 h-16 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Sales chart visualization</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Top Performing Products</CardTitle>
                  <CardDescription>Best sellers this month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {products.slice(0, 4).map((app, index) => (
                      <div key={app.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        {app.image && (
                          <img src={app.image} alt={app.name} className="w-12 h-12 rounded object-cover" />
                        )}
                        <div className="flex-1">
                          <div className="font-semibold">{app.name}</div>
                          <div className="text-sm text-gray-600">{app.downloads}</div>
                        </div>
                        <div className="font-bold text-orange-600">${app.price}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
